import { CallbackWrap } from '@/types/common'
import { Embed } from '@/types/embed'
import { LevelingEmbed, LevelingOverview } from '@/types/leveling'
import NumberInput from '@/ui/number'
import { faEdit, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Input } from 'components/editor/Input'
import React from 'react'
import './difficulty-bar.scss'

export const difficultyToColorMap = [
  undefined,
  '#851522',
  '#CE5927',
  '#F7AB3A',
  '#4B9257',
  '#057af0'
]

type LevelingDifficulty = Exclude<LevelingOverview['buildRating'], undefined>[0]

export function DifficultyBar({
  embed,
  onEmbedChange
}: {
  embed: LevelingEmbed
  onEmbedChange?: (value: CallbackWrap<Embed>) => void
}) {
  const content = embed?.overview?.buildRating || []
  const [isEditing, setIsEditing] = React.useState(false)

  const onAdd = React.useCallback(
    () =>
      onEmbedChange?.((embed) => {
        if (embed.type !== 'leveling') return embed
        embed = { ...embed }
        embed.overview = { ...embed.overview }
        embed.overview.buildRating = embed.overview.buildRating?.slice() ?? []

        embed.overview.buildRating.push({
          title: '',
          subtitle: '',
          difficulty: 1
        })
        return embed
      }),
    [onEmbedChange]
  )

  const onDelete = (index: number) =>
    onEmbedChange?.((embed) => {
      if (embed.type !== 'leveling') return embed
      embed = { ...embed }
      embed.overview = { ...embed.overview }
      embed.overview.buildRating = embed.overview.buildRating?.slice() ?? []
      embed.overview.buildRating.splice(index, 1)
      return embed
    })

  const onEditAttr = <K extends keyof LevelingDifficulty>(
    index: number,
    attr: K,
    newValue: LevelingDifficulty[K]
  ) =>
    onEmbedChange?.((embed) => {
      if (embed.type !== 'leveling') return embed
      embed = { ...embed }
      embed.overview = { ...embed.overview }
      embed.overview.buildRating = embed.overview.buildRating?.slice() ?? []
      embed.overview.buildRating[index] = {
        ...embed.overview.buildRating[index],
        [attr]: newValue
      }
      return embed
    })

  return (
    <div className="poe-difficulty-bar-wrapper">
      <div className="poe-title-box">
        Build Rating
        {onEmbedChange &&
          (isEditing ? (
            <div>
              <button onClick={() => setIsEditing(false)}>Close</button>
            </div>
          ) : (
            <div>
              <FontAwesomeIcon
                icon={faEdit}
                onClick={() => {
                  setIsEditing(!isEditing)
                }}
              />
            </div>
          ))}
      </div>

      {(Array.isArray(content) && content.length > 0) || onEmbedChange ? (
        <div className="poe-difficulty-bar">
          {isEditing ? (
            <div className="poe-difficulty-bar-edit">
              {content.map((item, index) => (
                <div key={index}>
                  <Input
                    placeholder="Title"
                    value={item.title}
                    onChange={(newValue) => {
                      onEditAttr(index, 'title', newValue)
                    }}
                  />
                  <Input
                    placeholder="Subtitle"
                    value={item.subtitle}
                    onChange={(newValue) => {
                      onEditAttr(index, 'subtitle', newValue)
                    }}
                  />
                  <NumberInput
                    value={item.difficulty}
                    min={1}
                    max={5}
                    step={1}
                    onChange={(newValue) =>
                      onEditAttr(
                        index,
                        'difficulty',
                        Math.min(Math.max(newValue, 1), 5)
                      )
                    }
                  />
                  <FontAwesomeIcon
                    icon={faTrashAlt}
                    onClick={() => {
                      onDelete(index)
                    }}
                  />
                </div>
              ))}
              <button onClick={onAdd}>Add</button>
            </div>
          ) : (
            content.map((item, index) => (
              <div key={index} className="poe-difficulty-bar-item">
                <span className="poe-title">{item.title}</span>

                <div className="poe-segments">
                  <div>
                    {[...Array(5)].map((_, segmentIndex) => (
                      <span
                        style={{
                          background:
                            segmentIndex < item.difficulty
                              ? difficultyToColorMap[item.difficulty]
                              : ''
                        }}
                        key={segmentIndex}></span>
                    ))}
                  </div>
                </div>

                <span
                  className="poe-subtitle"
                  style={{
                    color: difficultyToColorMap[item.difficulty]
                  }}>
                  {item.subtitle}
                </span>
              </div>
            ))
          )}
        </div>
      ) : null}
    </div>
  )
}
